import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Svg, Typography } from '@altermaind/web/ui';
import { Badge, VirtualizedTable } from '@/_shared/components';
import { getDepartmentMembersCall } from '@/departments/state/api';

type Props = {
  departmentName?: string;
  departmentId?: string;
  selectedEmployer: ReturnType<typeof getDepartmentMembersCall.hook>['allEmployees'];
  setSelectedEmployer: Dispatch<
    SetStateAction<ReturnType<typeof getDepartmentMembersCall.hook>['allEmployees']>
  >;
  goBack: () => void;
  onNext: (
    selectedEmployer: ReturnType<typeof getDepartmentMembersCall.hook>['allEmployees'],
  ) => void;
};

const TABLE_MIN_WIDTH = 600;

export const ChooseDepartmentEmployees = ({
  departmentId,
  departmentName,
  selectedEmployer,
  setSelectedEmployer,
  goBack,
  onNext,
}: Props) => {
  const t = useTranslations('settings.tabs.delegates.addDelegate.dialog.delegateEmployees');

  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  const { execute: executeGetDepartmentMembers, allEmployees } = getDepartmentMembersCall.hook();

  const onChecboxesSelected = useCallback(
    (selectedIds: string[]) => {
      setSelectedEmployer(allEmployees.filter(employee => selectedIds.includes(employee.id)));
    },
    [allEmployees, setSelectedEmployer],
  );

  useEffect(() => {
    if (!departmentId) {
      return;
    }
    executeGetDepartmentMembers({
      departmentId,
      excludeDelegates: true,
      completed: true,
    });

    const widthBox = (ref.current as HTMLDivElement)?.offsetWidth;
    const PADDING = 60;
    setWidth(Math.max(widthBox - PADDING, TABLE_MIN_WIDTH));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  return (
    <Box mt='xxs'>
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='p2/light' textAlign='center'>
          {t('description')}
        </Typography>
      </Box>
      <Box
        ref={ref}
        mt='m'
        border='1px solid'
        borderColor='primary/text'
        borderRadius='m'
        padding='s'
      >
        <Box display='flex' justifyContent='space-between' flexDirection='row' mb='s'>
          <Typography variant='p1/bold' textAlign='center'>
            {departmentName}
          </Typography>
          <Badge label={`${allEmployees ? allEmployees.length : 0} ${t('table.people')}`} />
        </Box>
        {!allEmployees || allEmployees.length === 0 ? (
          <Box mt='s'>
            <Typography variant='p2/light' textAlign='center'>
              {t('noResults')}
            </Typography>
          </Box>
        ) : (
          <VirtualizedTable
            tableWidth={width}
            showCheckboxes
            checkBoxesCallback={onChecboxesSelected}
            data={allEmployees}
            headerLocales='settings.tabs.delegates.addDelegate.dialog.delegateEmployees.table'
            columnWidths={{ name: 100 }}
          />
        )}
      </Box>
      <Box mt='m' justifyContent='space-between' flexDirection='row' display='flex'>
        <Button onClick={goBack} textAlign='left' variant='link/primary' mb='s' mt='none'>
          <Svg name='back' size='xs' />
          <Typography ml='xxxs' variant='p2'>
            {t('cta.goBack')}
          </Typography>
        </Button>
        <Button
          onClick={() => onNext(selectedEmployer)}
          textAlign='left'
          variant='primary'
          mb='s'
          mt='none'
          disabled={!selectedEmployer?.length}
        >
          {t('cta.next')}
        </Button>
      </Box>
    </Box>
  );
};
